import React from 'react';
import { Box, CardContent, Grid, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { MembershipItem } from '@spike/membership-model';
import { calculateUsage } from 'utils/EnrollmentUtils';
import { ACCESS_LEVEL_ADMIN_ID } from '@spike/model';
import { useAuth, usePermissions } from 'hooks';

interface MembershipItemsProps {
    items: MembershipItem[];
    classes: { [key: string]: string };
    isPackage?: boolean;
    onAdjustUsage?: () => void;
}

const MembershipItems: React.FC<MembershipItemsProps> = ({ items, classes, isPackage, onAdjustUsage }) => {
    const auth = useAuth();
    const permissions = usePermissions();

    const regularItems = React.useMemo(() => {
        return items.filter(item => {
            const discountAmount = Number(item.discountAmount || 0);
            const discountPercentage = Number(item.discountPercentage || 0);
            return discountAmount === 0 && discountPercentage === 0;
        });
    }, [items]);

    const discountedItems = React.useMemo(() => {
        return items.filter(item => {
            const discountAmount = Number(item.discountAmount || 0);
            const discountPercentage = Number(item.discountPercentage || 0);
            return discountAmount > 0 || discountPercentage > 0;
        });
    }, [items]);

    const renderColumnHeaders = () => (
        <Grid
            container
            className={classes.membershipItemsGrid}
        >
            <Grid
                item
                xs={6}
                className={classes.membershipItemsTitleLeft}
            >
                <Typography
                    variant="subtitle2"
                    className={classes.membershipItemsTitle}
                >
                    Item
                </Typography>
            </Grid>
            <Grid
                item
                xs={2}
                style={{ textAlign: 'center' }}
            >
                <Typography
                    variant="subtitle2"
                    className={classes.membershipItemsTitle}
                >
                    Qty
                </Typography>
            </Grid>
            <Grid
                item
                xs={4}
                className={classes.membershipItemsTitleRight}
            >
                <Typography
                    variant="subtitle2"
                    align="right"
                    className={classes.membershipItemsTitle}
                >
                    Usage
                </Typography>
            </Grid>
        </Grid>
    );

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
                {regularItems.length > 0 && (
                    <Box className={classes.cardStyle}>
                        <CardContent className={clsx(classes.cardContent, classes.cardWide)}>
                            <Box
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 10
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    className={classes.cardTitle}
                                    style={{ marginBottom: 0 }}
                                >
                                    Membership Items
                                </Typography>
                                {isPackage && onAdjustUsage && permissions.memberships.adjustUsage && (
                                    <button
                                        type="button"
                                        onClick={onAdjustUsage}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            fontSize: 15,
                                            fontWeight: 500,
                                            color: '#000',
                                            padding: 0
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPen} />
                                        Adjust Usage
                                    </button>
                                )}
                            </Box>
                            {renderColumnHeaders()}
                            {regularItems.map((item, idx) => {
                                const { used } = calculateUsage(item);
                                const avail = item.left ?? 0;
                                return (
                                    <Grid
                                        container
                                        key={idx}
                                        alignItems="center"
                                        className={classes.membershipItemsRow}
                                    >
                                        <Grid
                                            item
                                            xs={6}
                                        >
                                            <Typography
                                                variant="body1"
                                                className={classes.membershipItemsItemName}
                                            >
                                                {item.itemName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                className={classes.membershipItemsItemLabel}
                                            >
                                                {item.itemType}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={2}
                                            style={{ textAlign: 'center' }}
                                        >
                                            <Typography variant="body2">
                                                {item.unlimited ? 'Unlimited' : item.quantity}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={4}
                                        >
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="flex-end"
                                                style={{ gap: 4 }}
                                            >
                                                {!item.unlimited && (
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            background: '#F4F4F4',
                                                            borderRadius: 6,
                                                            padding: '2px 10px'
                                                        }}
                                                    >
                                                        Avail: {avail}
                                                    </Typography>
                                                )}
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: '#7A7A7A', fontSize: 13 }}
                                                >
                                                    Used: {used}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </CardContent>
                    </Box>
                )}

                {discountedItems.length > 0 && (
                    <Box className={classes.cardStyle}>
                        <CardContent className={clsx(classes.cardContent, classes.cardWide)}>
                            <Typography
                                variant="h6"
                                className={classes.cardTitle}
                            >
                                Discounted Items
                            </Typography>
                            {renderColumnHeaders()}
                            {discountedItems.map((item, idx) => {
                                const { used } = calculateUsage(item);
                                return (
                                    <Grid
                                        container
                                        key={idx}
                                        alignItems="center"
                                        className={classes.membershipItemsRow}
                                    >
                                        <Grid
                                            item
                                            xs={6}
                                        >
                                            <Typography
                                                variant="body1"
                                                className={classes.membershipItemsItemName}
                                            >
                                                {item.itemName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                className={classes.membershipItemsItemLabel}
                                            >
                                                {item.itemType}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={2}
                                            style={{ textAlign: 'center' }}
                                        >
                                            <Typography variant="body2">Unlimited</Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={4}
                                        >
                                            <Box
                                                display="flex"
                                                justifyContent="flex-end"
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: '#7A7A7A', fontSize: 13 }}
                                                >
                                                    Used: {used}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </CardContent>
                    </Box>
                )}
            </div>
        </>
    );
};

export default MembershipItems;
